import type { ComputedRef } from 'vue'
import type { ChangelogActionRow, ChangelogItemRow } from '@entities/changelog'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { useChangelogApi, useChangelogStore } from '@entities/changelog'
import { useDayjs } from '@shared/lib/format'

import { useChangelogItemsApi } from '../api/items.hook'
import { getChangelogActionLabel } from './changelog-options'

interface TableChangelogReturn {
  actions: ComputedRef<ChangelogActionRow[]>
  isLoading: ComputedRef<boolean>
  hasActions: ComputedRef<boolean>
  hasNext: ComputedRef<boolean>
  hasPrev: ComputedRef<boolean>
  perPage: ComputedRef<number>
  count: ComputedRef<number>
  expandedActionId: ComputedRef<number | null>
  items: ComputedRef<ChangelogItemRow[]>
  isItemsLoading: ComputedRef<boolean>
  formatDateTime: (value: string | null) => string
  getActionLabel: (row: ChangelogActionRow) => string
  getItemChange: (item: ChangelogItemRow) => string
  toggleItems: (row: ChangelogActionRow) => Promise<void>
  setChangePerPage: (value: number) => Promise<void>
  goNext: () => Promise<void>
  goPrev: () => Promise<void>
}

export const useTableChangelog = (): TableChangelogReturn => {
  const router = useRouter()
  const route = useRoute()
  const store = useChangelogStore()
  const dayjs = useDayjs()

  const actions = computed(() => store.getActions)
  const isLoading = computed(() => store.isLoading)
  const hasActions = computed(() => store.hasActions)
  const hasNext = computed(() => store.hasNext)
  const hasPrev = computed(() => store.hasPrev)
  const perPage = computed(() => store.getPerPage)
  const count = computed(() => store.getActions.length)
  const expandedActionId = computed(() => store.getExpandedActionId)
  const items = computed(() => store.getItems)
  const isItemsLoading = computed(() => store.isItemsLoading)

  const formatDateTime = (value: string | null): string =>
    value ? dayjs(value).format(DateFormat.KanbanDate) : '—'

  const getActionLabel = (row: ChangelogActionRow): string => getChangelogActionLabel(row.action)

  const getItemChange = (item: ChangelogItemRow): string => {
    const [instance, field] = item.field.split(':')
    const label = `${(instance ?? '').charAt(0).toUpperCase()}${(instance ?? '').slice(1)} ${field ?? ''}`.trim()

    return `${label} was ${item.action}`
  }

  const setChangePerPage = async (value: number): Promise<void> => {
    store.setPerPage(value)
    store.setCursor(null)
    store.setExpandedActionId(null)
    await router.push({
      query: {
        ...route.query,
        per_page: String(value),
        cursor: undefined,
      },
    })
    await useChangelogApi()
  }

  const goToCursor = async (cursor: string | null): Promise<void> => {
    if (!cursor) {
      return
    }

    store.setCursor(cursor)
    store.setExpandedActionId(null)
    await router.push({
      query: {
        ...route.query,
        cursor,
      },
    })
    await useChangelogApi()
  }

  const goNext = (): Promise<void> => goToCursor(store.getNextCursor)
  const goPrev = (): Promise<void> => goToCursor(store.getPrevCursor)

  const toggleItems = async (row: ChangelogActionRow): Promise<void> => {
    if (store.getExpandedActionId === row.id) {
      store.setExpandedActionId(null)
      return
    }

    store.setExpandedActionId(row.id)
    await useChangelogItemsApi(row.id)
  }

  return {
    actions,
    isLoading,
    hasActions,
    hasNext,
    hasPrev,
    perPage,
    count,
    expandedActionId,
    items,
    isItemsLoading,
    formatDateTime,
    getActionLabel,
    getItemChange,
    toggleItems,
    setChangePerPage,
    goNext,
    goPrev,
  }
}
